import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface UserRole {
  role: 'admin' | 'expert' | 'pro' | 'user' | 'guest';
  userLevel: string;
  ztartagPlan: string;
  permissions: {
    canAccessAdmin: boolean;
    canViewAnalytics: boolean;
    canManageProviders: boolean;
    canUsePremiumModels: boolean;
    canUseStreaming: boolean;
    canAccessMetaDashboard: boolean;
    metaDashboardLevel: 'full' | 'read_write' | 'read_only' | 'restricted' | 'none';
    metaDashboardPermissions: Record<string, any>;
    dailyTokenLimit: number;
    monthlyTokenLimit: number;
    maxCostPerDay: number;
    // Administrative permissions
    canManageUsers: boolean;
    canManageSystem: boolean;
    canViewAllData: boolean;
    canManageFinances: boolean;
    canImpersonateUsers: boolean;
    canBypassLimits: boolean;
    // Business & Enterprise access
    canAccessBusinessFeatures: boolean;
    canAccessEnterpriseFeatures: boolean;
    canManageBusinessAccounts: boolean;
    canViewSystemMetrics: boolean;
    canManageSecuritySettings: boolean;
  };
}

const defaultPermissions = {
  canAccessAdmin: false,
  canViewAnalytics: false,
  canManageProviders: false,
  canUsePremiumModels: false,
  canUseStreaming: false,
  canAccessMetaDashboard: false,
  metaDashboardLevel: 'none' as const,
  metaDashboardPermissions: {},
  dailyTokenLimit: 1000,
  monthlyTokenLimit: 30000,
  maxCostPerDay: 1,
  // Administrative permissions
  canManageUsers: false,
  canManageSystem: false,
  canViewAllData: false,
  canManageFinances: false,
  canImpersonateUsers: false,
  canBypassLimits: false,
  // Business & Enterprise access
  canAccessBusinessFeatures: false,
  canAccessEnterpriseFeatures: false,
  canManageBusinessAccounts: false,
  canViewSystemMetrics: false,
  canManageSecuritySettings: false
};

export function useUserRole() {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserRole();
  }, []);

  const loadUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setUserRole({
          role: 'guest',
          userLevel: 'guest',
          ztartagPlan: 'free',
          permissions: defaultPermissions
        });
        setLoading(false);
        return;
      }

      // Obtener rol del usuario
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      // Obtener perfil del usuario
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_level, ztartag_plan')
        .eq('id', user.id)
        .single();

      const role = userRoles?.[0]?.role || 'user';
      
      // Definir permisos según el rol
      const permissions = await getPermissionsByRole(role, user.id);

      setUserRole({
        role,
        userLevel: profile?.user_level || 'guest',
        ztartagPlan: profile?.ztartag_plan || 'free',
        permissions
      });

    } catch (error) {
      console.error('Error loading user role:', error);
      setUserRole({
        role: 'guest',
        userLevel: 'guest',
        ztartagPlan: 'free',
        permissions: defaultPermissions
      });
    } finally {
      setLoading(false);
    }
  };

  const getPermissionsByRole = async (role: string, userId: string) => {
    // Verificar acceso al Meta-Dashboard usando la función de la BD
    let metaDashboardAccess = { has_access: false, access_level: 'none', permissions: {} };
    
    try {
      const { data } = await supabase.rpc('can_access_meta_dashboard', { p_user_id: userId });
      if (data && data.length > 0) {
        metaDashboardAccess = data[0];
      }
    } catch (error) {
      console.error('Error checking Meta-Dashboard access:', error);
    }

    const basePermissions = (() => {
      switch (role) {
        case 'admin':
          return {
            canAccessAdmin: true,
            canViewAnalytics: true,
            canManageProviders: true,
            canUsePremiumModels: true,
            canUseStreaming: true,
            dailyTokenLimit: 10000000, // 10M tokens per day
            monthlyTokenLimit: 300000000, // 300M tokens per month
            maxCostPerDay: 10000, // $10,000 per day
            // Full administrative permissions
            canManageUsers: true,
            canManageSystem: true,
            canViewAllData: true,
            canManageFinances: true,
            canImpersonateUsers: true,
            canBypassLimits: true,
            // Complete Business & Enterprise access
            canAccessBusinessFeatures: true,
            canAccessEnterpriseFeatures: true,
            canManageBusinessAccounts: true,
            canViewSystemMetrics: true,
            canManageSecuritySettings: true
          };
        case 'expert':
          return {
            canAccessAdmin: true,
            canViewAnalytics: true,
            canManageProviders: false,
            canUsePremiumModels: true,
            canUseStreaming: true,
            dailyTokenLimit: 500000,
            monthlyTokenLimit: 15000000,
            maxCostPerDay: 500,
            // Limited administrative permissions
            canManageUsers: false,
            canManageSystem: false,
            canViewAllData: true,
            canManageFinances: false,
            canImpersonateUsers: false,
            canBypassLimits: false,
            // Limited Business access
            canAccessBusinessFeatures: true,
            canAccessEnterpriseFeatures: false,
            canManageBusinessAccounts: false,
            canViewSystemMetrics: true,
            canManageSecuritySettings: false
          };
        case 'pro':
          return {
            canAccessAdmin: true,
            canViewAnalytics: false,
            canManageProviders: false,
            canUsePremiumModels: true,
            canUseStreaming: true,
            dailyTokenLimit: 100000,
            monthlyTokenLimit: 3000000,
            maxCostPerDay: 100,
            // No administrative permissions
            canManageUsers: false,
            canManageSystem: false,
            canViewAllData: false,
            canManageFinances: false,
            canImpersonateUsers: false,
            canBypassLimits: false,
            // Basic Business access
            canAccessBusinessFeatures: true,
            canAccessEnterpriseFeatures: false,
            canManageBusinessAccounts: false,
            canViewSystemMetrics: false,
            canManageSecuritySettings: false
          };
        case 'user':
          return {
            canAccessAdmin: false,
            canViewAnalytics: false,
            canManageProviders: false,
            canUsePremiumModels: false,
            canUseStreaming: false,
            dailyTokenLimit: 10000,
            monthlyTokenLimit: 300000,
            maxCostPerDay: 10,
            // No administrative permissions
            canManageUsers: false,
            canManageSystem: false,
            canViewAllData: false,
            canManageFinances: false,
            canImpersonateUsers: false,
            canBypassLimits: false,
            // No Business/Enterprise access
            canAccessBusinessFeatures: false,
            canAccessEnterpriseFeatures: false,
            canManageBusinessAccounts: false,
            canViewSystemMetrics: false,
            canManageSecuritySettings: false
          };
        default:
          return {
            canAccessAdmin: false,
            canViewAnalytics: false,
            canManageProviders: false,
            canUsePremiumModels: false,
            canUseStreaming: false,
            dailyTokenLimit: 1000,
            monthlyTokenLimit: 30000,
            maxCostPerDay: 1,
            // No administrative permissions
            canManageUsers: false,
            canManageSystem: false,
            canViewAllData: false,
            canManageFinances: false,
            canImpersonateUsers: false,
            canBypassLimits: false,
            // No Business/Enterprise access
            canAccessBusinessFeatures: false,
            canAccessEnterpriseFeatures: false,
            canManageBusinessAccounts: false,
            canViewSystemMetrics: false,
            canManageSecuritySettings: false
          };
      }
    })();

    return {
      ...basePermissions,
      canAccessMetaDashboard: metaDashboardAccess.has_access,
      metaDashboardLevel: metaDashboardAccess.access_level as 'full' | 'read_write' | 'read_only' | 'restricted' | 'none',
      metaDashboardPermissions: metaDashboardAccess.permissions || {}
    };
  };

  const hasPermission = (permission: keyof UserRole['permissions']) => {
    return userRole?.permissions[permission] || false;
  };

  const canAccess = (feature: 'admin' | 'analytics' | 'providers' | 'premium' | 'streaming' | 'metaDashboard') => {
    if (!userRole) return false;
    
    switch (feature) {
      case 'admin':
        return userRole.permissions.canAccessAdmin;
      case 'analytics':
        return userRole.permissions.canViewAnalytics;
      case 'providers':
        return userRole.permissions.canManageProviders;
      case 'premium':
        return userRole.permissions.canUsePremiumModels;
      case 'streaming':
        return userRole.permissions.canUseStreaming;
      case 'metaDashboard':
        return userRole.permissions.canAccessMetaDashboard;
      default:
        return false;
    }
  };

  return {
    userRole,
    loading,
    hasPermission,
    canAccess,
    refreshRole: loadUserRole
  };
}